import type { LDContext, LDContextCommon } from "@launchdarkly/node-server-sdk";

/**
 * Feature flag keys sourced from LaunchDarkly. Each member's value must match
 * the corresponding flag key configured in LaunchDarkly exactly.
 */
export enum Flag {
  OPEN_ENROLLMENT_CHAT_OPTION_ENABLED = "open-enrollment-chat-option",
  ENDPOINT_CDP_PROVIDER_OVERRIDE = "endpoint-cdp-provider-override",
  RPA_PROXY_ASSIGNMENT = "rpa-proxy-assignment",
  SIGN_IN_HEDGE_COUNT = "sign-in-hedge-count",
  GENERATE_REVIEW_FINDING_TASKS = "generate-review-finding-tasks",
  PLAYWRIGHT_CAPTURE_HAR_ARTIFACT = "playwright-capture-har-artifact",
  PLAYWRIGHT_FILL_STRATEGY = "playwright-fill-strategy",
  PLAYWRIGHT_MOVE_CURSOR_STRATEGY = "playwright-move-cursor-strategy",
  BROWSERBASE_CUSTOM_SESSION_SETTINGS = "browserbase-custom-session-settings",
  STEEL_USE_STEALTH_BROWSER = "steel-use-stealth-browser",
  RUN_CLASSIC_CRAWL = "run-classic-crawl",
  CLASSIC_CRAWL_REQUESTS_PER_SECOND = "classic-crawl-requests-per-second",
  RUN_AI_CRAWL = "run-ai-crawl",
  CRAWL_ENABLE_DISCOVERY = "crawl-enable-discovery",
  COMPARE_FAIR_MARKET_VALUE = "compare-fair-market-value",
  AI_CRAWL_ENABLE_CLAIMS_LIST_DISCOVERY = "ai-crawl-enable-claims-list-discovery",
  AI_CRAWL_ENABLE_CLAIM_DETAILS_VISITS = "ai-crawl-enable-claim-details-visits",
  AI_CRAWL_ENABLE_PLAN_DOCUMENTS = "ai-crawl-enable-plan-documents",
  AI_CRAWL_ENABLE_MEMBER_ID_CARDS = "ai-crawl-enable-member-id-cards",
  AI_CRAWL_ENABLE_MFA_CONFIGURATION_DISCOVERY = "ai-crawl-enable-mfa-configuration-discovery",
  AI_CRAWL_ENABLE_ACCUMULATORS_DISCOVERY = "ai-crawl-enable-accumulators-discovery",
  AI_CRAWL_ENABLE_PARALLELIZATION = "enable-ai-crawl-parallelization",
  INGESTION_V2_MARKDOWN_PIPELINE = "ingestion-v2-markdown-pipeline",
  /** Routes documentUpload to the V2-only Temporal workflow (case-aggregate
   *  + UserDataView + UserEnrichmentData) instead of the legacy
   *  documentUploadWorkflow (V1 LLM extraction + IssueDocumentSummary). */
  DOC_SUMMARY_FROM_INGESTION_V2 = "doc-summary-from-ingestion-v2",
  AI_CRAWL_FILE_GRABBER_MODEL = "ai-crawl-file-grabber-model",
  AI_CRAWL_STEPPER_V2_MODEL = "ai-crawl-stepper-v2-model",
  AI_CRAWL_LARGE_MODEL = "ai-crawl-large-model",
  AI_CRAWL_SMALL_MODEL = "ai-crawl-small-model",
  SIGN_IN_ROBOT_AI_MODEL = "sign-in-robot-ai-model",
  SIGN_IN_LOGIN_SENTINEL_MODEL = "sign-in-login-sentinel-model",
  PARSE_COMPLETER_MODEL = "parse-completer-model",
  SIGN_IN_FUSE_MFA_CODE_SUBMISSION = "sign-in-fuse-mfa-code-submission",
  SIGN_IN_APPLY_LINK_CREATION_ACCESS_POLICIES = "sign-in-apply-link-creation-access-policies",
  SIGN_IN_MULTI_BOX_MFA = "sign-in-multi-box-mfa",
  SIGN_IN_CONCURRENT_VIEWS = "sign-in-concurrent-views",
  SIGN_IN_STRICT_MODE = "sign-in-strict-mode",
  SIGN_IN_AI_PROMPT = "sign-in-ai-prompt",
  SIGN_IN_TIME_OUT_IO_TOOLS = "sign-in-timeout-io-tools",
  SIGN_IN_NEOCLASSICALLY = "sign-in-neoclassically",
  SIGN_IN_HUMANIZE_CURSOR = "sign-in-humanize-cursor",
  SIGN_IN_ATTEMPT_CONTINUE = "sign-in-attempt-continue",
  SIGN_IN_LAZY = "sign-in-lazy",
  SIGN_IN_TAKEOVER_HOLD = "sign-in-takeover-hold",
  NEO_FALLBACK_AGENT = "neo-fallback-agent",
  NEO_EXTRA_SUPERVISION = "neo-extra-supervision",
  INGESTION_DATA_QUALITY_ENABLED = "ingestion-data-quality-enabled",
  AI_CRAWL_ENABLE_CLAIM_PDF_VISITS = "ai-crawl-enable-claim-pdf-visits",
  EOB_FINDINGS_V1_ENABLED = "eob-findings-v1",
  AUTO_ANALYZE_SIGN_IN_LOGS = "auto-analyze-sign-in-logs",
  REMOVE_INSURANCE_FROM_ONBOARDING = "remove-insurance-from-onboarding",
  SHOW_PROMO_CODE_INPUT = "show-promo-code-input",
  SHOW_PAYMENT_IN_SETTINGS = "show-payment-in-settings",
  AI_CRAWL_SPECIALIST_INSTRUCTIONS_REF = "ai-crawl-specialist-instructions-ref",
  BOT_CONTROL_ENABLED = "bot-control-enabled",
  VELLUM_BENEFIT_SUMMARY_RELEASE_TAG = "vellum-benefit-summary-release-tag",
  BENEFIT_SUMMARY_WORKFLOW_ENABLED = "benefit-summary-workflow-enabled",
  OAUTH_ENABLED = "o-auth-enabled",
  VONAGE_SMS_CC_EMAILS = "vonage-sms-cc-emails",
  PLAN_DOCUMENTS_LOADING_UI_TIMEOUT_SECONDS = "plan-documents-loading-ui-timeout-seconds",
  INSURANCE_LINKING_PROGRESS_TEXTS = "insurance-linking-progress-texts",
  SHOW_P3_ENDPOINTS = "show-p3-endpoints",
  FORCE_SHOW_WELCOME_SCREEN = "force-show-welcome-screen",
  CASE_AUTOMATION_ROUTER_WORKFLOW = "case-automation-router-workflow",
  CASE_AUTOMATION_SPECIAL_MESSAGING = "case-automation-special-messaging",
  CASE_AUTOMATION_RECORD_FACT_TOOL = "case-automation-record-fact-tool",
  CASE_AUTOMATION_MULTI_SELECTION_TOOL = "case-automation-multi-selection-tool",
  CASE_AUTOMATION_REQUEST_USER_INPUT_TOOL = "case-automation-request-user-input-tool",
  HA_FEEDBACK_SLACK_CHANNEL = "ha-feedback-slack-channel",
  HIPAA_RELEASE_V3 = "hipaa-release-v3",
  FORCE_CASE_AUTOMATION_PRE_AUTH_WORKFLOW = "force-case-automation-pre-auth-workflow",
  BILL_CHECKER_INVOICES_PRICE_TRANSPARENCY = "bill-checker-invoices-price-transparency",
  CASE_AUTOMATION_HIPAA_INSURER_RESOLUTION = "case-automation-hipaa-insurer-resolution",
  CASE_AUTOMATION_REALTIME_STREAMING = "case-automation-realtime-streaming",
  CASE_NORMAL_SCROLL_ON_STAFF = "case-normal-scroll-on-staff",
  DEFERRED_AUTOMATION_BACKSTOP_DELAY_MS = "deferred-automation-backstop-delay-ms",
  SHOW_POST_ONBOARDING_REFERRAL_MODAL = "show-post-onboarding-referral-modal",
  SHOW_EHR_DEFAULT_LINKS = "show-ehr-default-links",
  EXPEDITE_BILL_UPLOAD = "expedite-bill-upload",
  CASE_AUTOMATION_RESOLUTION_CONFIDENCE = "case-automation-resolution-confidence",
  ENABLE_MEMBERSHIP_CARD_ENRICHMENT = "enable-membership-card-enrichment",
  DOCUMENT_ANALYSIS_LOADING_CONFIG = "document-analysis-loading-config",
  NEW_CASELIST_UI = "new-caselist-ui",
  POST_ESCALATION_ACTIONS_V1 = "post-escalation-actions-v1",
  CASE_AUTOMATION_MODEL_OVERRIDES = "case-automation-model-overrides",
  CASE_AUTOMATION_EXTENDED_THINKING = "case-automation-extended-thinking",
  CASE_AUTOMATION_TURN_EXTRACTION = "case-automation-turn-extraction",
  BILL_PAY = "bill-pay",
  FAKE_DOOR_MMFA = "fake-door-mmfa",
  NOTIFICATION_DEDUCTIBLE_CHANGED_MIN_DOLLARS = "notification-deductible-changed-min-dollars",
  NOTIFICATION_DEDUCTIBLE_CHANGED_SUPPRESSION_HOURS = "notification-deductible-changed-suppression-hours",
  CASE_RESOLUTION_INTELLIGENCE = "case-resolution-intelligence",
  WEB_AGENT_PROVIDER_MANAGED_PERSISTENCE = "web-agent-provider-managed-persistence",
  USE_ONBOARDING_FLOW = "use-onboarding-flow",
  HA_ASSIGNED_EVENT_ENABLED = "ha-assigned-event-enabled",
  COMPUTE_HA_AVERAGE_RATING = "compute-ha-average-rating",
  CHAT_ERROR_ALERTS = "chat-error-alerts",
  /** Reads the bills list + bill detail from the ingestion health-data
   *  projection (UserHealthRecord / v2.EOB + v2.Invoice) instead of the v1
   *  FHIR EOB path. */
  BILLS_FROM_HEALTH_DATA = "bills-from-health-data",
}

export type Contexts =
  | "endpointSlug"
  | "user"
  | "toPhoneNumber"
  | "executionId";

/** Acceptable contexts for LaunchDarkly. */
export type KnownLDContext = { kind: "multi" } & {
  // This enforces that at least one of the Context properties is present.
  // Otherwise, LD's client will error at runtime.
  [K in Contexts]: { [P in K]: LDContextCommon } & {
    [P in Exclude<Contexts, K>]?: LDContextCommon;
  };
}[Contexts];

({}) as KnownLDContext satisfies LDContext;

export const EMPTY_LD_CONTEXT: KnownLDContext = {
  kind: "multi",
  user: { key: "empty" },
};
